import i18n from "../../i18n";import { Trans } from "react-i18next";import React, { useCallback, useEffect, useState } from "react";
import "./positionHistory.css";
import { resolveApiUrl } from "../../api/api";
import sessionDB from "../../utils/sessionDB";
import {
  buildOnboardingAccessMessage,
  isOnboardingFeatureUnavailableError,
  isGenericOnboardingErrorMessage,
  parseOnboardingErrorResponse,
  handleOnboardingApiError,
  resolveOnboardingActionConfig } from
"./onboardingFeatureAccess";

const ADD_ENDPOINT = "/saas/hrms/position-history/add";
const ADD_SERVICE_CODE = "HR03A";
const UPDATE_SERVICE_CODE = "HR03U";
const SEARCH_SERVICE_CODE = "HR03S";
const EMP_TYPES = ["Permanent", "Probationary", "Contract", "Intern", "Consultant"];

const EMP_TYPE_OPTION_KEYS = {
  Permanent: "onboarding.options.empType.permanent",
  Probationary: "onboarding.options.empType.probationary",
  Contract: "onboarding.options.empType.contract",
  Intern: "onboarding.options.empType.intern",
  Consultant: "onboarding.options.empType.consultant"
};

const FIELD_LABEL_KEYS = {
  empId: "common.employeeId",
  effectiveDate: "dateTime.effectiveDate",
  empType: "onboarding.employeeType",
  roleId: "onboarding.roleId",
  designationId: "onboarding.designationId",
  departmentId: "onboarding.departmentId",
  gradeLevelId: "onboarding.gradeLevelId",
  divisionId: "onboarding.divisionId",
  costCenterId: "onboarding.costCenterId",
  workLocationId: "onboarding.workLocationId",
  managerId: "onboarding.managerId"
};

function getFieldLabel(name) {
  const key = FIELD_LABEL_KEYS[name];
  return key ? i18n.t(key) : name;
}

function translateEmpTypeLabel(value) {
  const key = EMP_TYPE_OPTION_KEYS[value];
  return key ? i18n.t(key) : value;
}

const INITIAL_FORM = {
  empId: "",
  effectiveDate: "",
  empType: "",
  roleId: "",
  designationId: "",
  departmentId: "",
  gradeLevelId: "",
  divisionId: "",
  costCenterId: "",
  workLocationId: "",
  managerId: ""
};

/** Convert API / form dates into yyyy-MM-dd for <input type="date">. */
function toInputDate(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed || trimmed === "null" || trimmed === "undefined") return "";
  let str = trimmed.includes("T") ? trimmed.split("T")[0] : trimmed;
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [day, month, year] = str.split("-");
    return `${year}-${month}-${day}`;
  }
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
    const [day, month, year] = str.split("/");
    return `${year}-${month}-${day}`;
  }
  return "";
}

/** Backend update path expects effectiveDate as dd-MM-yyyy. */
function toApiEffectiveDate(value) {
  const iso = toInputDate(value);
  if (!iso) return "";
  const [year, month, day] = iso.split("-");
  return `${day}-${month}-${year}`;
}

function isValidIsoDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || "").trim());
}

function isNumericValue(value) {
  return /^\d+$/.test(String(value || "").trim());
}

/* eslint-disable-next-line no-unused-vars -- retained for API error normalization */
function normalizePositionHistoryErrorMessage(message) {
  return String(message || "").trim() || i18n.t("onboarding.errors.positionHistoryRequestFailed");
}

export default function PositionHistory({
  accessToken,
  tenantCode,
  initialEmployeeId = "",
  breadcrumb,
  onBack,
  onNext,
  onSuccess,
  isTabMode = false,
  isActive = true,
  saveTriggerRef
}) {
  useEffect(() => {
    if (saveTriggerRef) {
      saveTriggerRef.current = {
        submit: handleSave
      };
    }
  });

  const [form, setForm] = useState(() => ({
    ...INITIAL_FORM,
    empId: String(initialEmployeeId || "")
  }));
  const [loading, setLoading] = useState(false);
  const [banner, _setBanner] = useState(null);
  const setBanner = (val) => {
    _setBanner((prev) => {
      const next = typeof val === "function" ? val(prev) : val;
      if (next && next.type === "error") {
        if (isOnboardingFeatureUnavailableError(next.msg)) {
          return { type: "error", msg: i18n.t("onboarding.featureUnavailable") };
        }
        if (isGenericOnboardingErrorMessage(next.msg)) {
          return prev;
        }
      }
      return next;
    });
  };
  const [errors, setErrors] = useState({});
  const [isUpdate, setIsUpdate] = useState(false);
  /** Original effective date from backend — used as the update path key. */
  const [originalEffectiveDate, setOriginalEffectiveDate] = useState("");

  const sessionAccessToken = sessionDB.getAccessToken() || accessToken || "";
  const tenantId = sessionDB.getTenantId();
  const tenantCodeFromSession = sessionDB.getTenantCode() || tenantCode || "";

  const fetchPosition = useCallback(async () => {
    if (!initialEmployeeId) return;
    try {
      const headers = {
        "x-tenant-code": tenantCodeFromSession || "",
        "x-tenant-id": String(tenantId || ""),
        "x-service-code": SEARCH_SERVICE_CODE,
        authorization: `Bearer ${sessionAccessToken}`,
        Accept: "application/json"
      };

      const response = await fetch(
        resolveApiUrl(`/saas/hrms/position-history/search/${Number(initialEmployeeId)}`),
        { method: "GET", headers }
      );

      if (!response.ok) return;

      const body = await response.json();
      const data = body.data || body;
      const candidates = [data?.content, data?.records, data?.items, data?.data, data];
      const arrayCandidate = candidates.find((c) => Array.isArray(c));
      const record = arrayCandidate ?
      arrayCandidate[0] :
      data && typeof data === "object" && !Array.isArray(data) ?
      data :
      null;

      if (!record || typeof record !== "object") return;

      const effectiveInputDate = toInputDate(record.effectiveDate);
      setForm({
        empId: String(initialEmployeeId),
        effectiveDate: effectiveInputDate,
        empType: record.empType || "",
        roleId: String(record.roleId ?? ""),
        designationId: String(record.designationId ?? ""),
        departmentId: String(record.departmentId ?? ""),
        gradeLevelId: String(record.gradeLevelId ?? ""),
        divisionId: String(record.divisionId ?? ""),
        costCenterId: String(record.costCenterId ?? ""),
        workLocationId: String(record.workLocationId ?? ""),
        managerId: String(record.managerId ?? "")
      });
      setOriginalEffectiveDate(toApiEffectiveDate(record.effectiveDate) || toApiEffectiveDate(effectiveInputDate));
      setIsUpdate(true);
    } catch (err) {
      console.error("Failed to load position history info", err);
    }
  }, [initialEmployeeId, tenantCodeFromSession, tenantId, sessionAccessToken]);

  useEffect(() => {
    if (!isActive) return;
    if (!initialEmployeeId) {
      setForm({
        ...INITIAL_FORM,
        empId: ""
      });
      setIsUpdate(false);
      setOriginalEffectiveDate("");
      return;
    }
    setForm((prev) => ({ ...prev, empId: String(initialEmployeeId) }));
    fetchPosition();
  }, [isActive, initialEmployeeId, fetchPosition]);

  function validateForm(nextForm = form) {
    const nextErrors = {};

    if (!String(nextForm.empId || "").trim()) {
      nextErrors.empId = i18n.t("onboarding.errors.empIdRequired");
    } else if (!isNumericValue(nextForm.empId)) {
      nextErrors.empId = i18n.t("onboarding.errors.empIdMustBeNumeric");
    }

    if (!nextForm.effectiveDate) {
      nextErrors.effectiveDate = i18n.t("onboarding.errors.effectiveDateRequired");
    } else if (!isValidIsoDate(nextForm.effectiveDate)) {
      nextErrors.effectiveDate = i18n.t("onboarding.errors.useValidDate");
    }

    if (!nextForm.empType) {
      nextErrors.empType = i18n.t("onboarding.errors.employeeTypeRequired");
    } else if (!EMP_TYPES.includes(nextForm.empType)) {
      nextErrors.empType = i18n.t("onboarding.errors.chooseValidEmployeeType");
    }

    [
    "roleId",
    "designationId",
    "departmentId",
    "gradeLevelId",
    "divisionId",
    "costCenterId",
    "workLocationId",
    "managerId"]
    .forEach((field) => {
      const label = getFieldLabel(field);
      if (!String(nextForm[field] || "").trim()) {
        nextErrors[field] = i18n.t("validation.fieldRequired", { field: label });
      } else if (!isNumericValue(nextForm[field])) {
        nextErrors[field] = i18n.t("onboarding.errors.fieldMustBeNumeric", { label });
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  }

  async function handleSave() {
    setBanner(null);

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const empId = Number(String(form.empId).trim());
      const effectiveDateForPath = isUpdate ?
      originalEffectiveDate || toApiEffectiveDate(form.effectiveDate) :
      toApiEffectiveDate(form.effectiveDate);

      if (isUpdate && !effectiveDateForPath) {
        throw new Error(i18n.t("onboarding.errors.effectiveDateRequiredForUpdate"));
      }

      // Build URLs explicitly — do not rely on feature-config templates that omit effectiveDate.
      const addConfig = resolveOnboardingActionConfig({
        submoduleNames: ["Position History"],
        action: "add",
        fallback: {
          title: "Add Position History",
          serviceCode: ADD_SERVICE_CODE,
          endpoint: ADD_ENDPOINT,
          method: "POST"
        }
      });
      const updateConfig = resolveOnboardingActionConfig({
        submoduleNames: ["Position History"],
        action: "update",
        fallback: {
          title: "Update Position History",
          serviceCode: UPDATE_SERVICE_CODE,
          endpoint: `/saas/hrms/position-history/update/{empId}/{effectiveDate}`,
          method: "PUT"
        }
      });

      const endpoint = isUpdate ?
      `/saas/hrms/position-history/update/${empId}/${encodeURIComponent(effectiveDateForPath)}` :
      ADD_ENDPOINT;
      const method = isUpdate ? "PUT" : "POST";
      const serviceCode = isUpdate ?
      updateConfig.serviceCode || UPDATE_SERVICE_CODE :
      addConfig.serviceCode || ADD_SERVICE_CODE;

      if (!(isUpdate ? updateConfig.isAvailable : addConfig.isAvailable)) {
        throw new Error(
          buildOnboardingAccessMessage(
            i18n.t("onboarding.positionHistory"),
            isUpdate ? i18n.t("common.update") : i18n.t("common.add")
          )
        );
      }

      const payload = {
        empId,
        effectiveDate: form.effectiveDate,
        empType: form.empType,
        roleId: Number(form.roleId.trim()),
        designationId: Number(form.designationId.trim()),
        departmentId: Number(form.departmentId.trim()),
        gradeLevelId: Number(form.gradeLevelId.trim()),
        divisionId: Number(form.divisionId.trim()),
        costCenterId: Number(form.costCenterId.trim()),
        workLocationId: Number(form.workLocationId.trim()),
        managerId: Number(form.managerId.trim())
      };

      const response = await fetch(resolveApiUrl(endpoint), {
        method,
        headers: {
          "Content-Type": "application/json",
          "x-tenant-code": tenantCodeFromSession,
          "x-tenant-id": String(tenantId || ""),
          "x-service-code": serviceCode,
          authorization: `Bearer ${sessionAccessToken}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const { errorMessage, errorBody } = await parseOnboardingErrorResponse(response);
        handleOnboardingApiError(errorBody, errorMessage, { setErrors, setBanner });
        return;
      }

      setBanner({ type: "success", msg: i18n.t("onboarding.messages.positionHistorySavedSuccessfully") });
      setIsUpdate(true);
      setOriginalEffectiveDate(toApiEffectiveDate(form.effectiveDate));
      if (isUpdate || initialEmployeeId) {
        await fetchPosition();
      }
      if (onSuccess) onSuccess();
    } catch (error) {
      handleOnboardingApiError(null, error.message, { setErrors, setBanner });
    } finally {
      setLoading(false);
    }
  }

  function handleFinish() {
    if (onNext) {
      onNext();
    }
  }

  return (
    <div className={`phs-page${isTabMode ? " is-tab-mode" : ""}`}>
      <main className="phs-main">
        {!isTabMode && breadcrumb &&
        <div className="phs-breadcrumb" aria-label={i18n.t("common.breadcrumb")}>
            {breadcrumb.map((item, index) =>
          <React.Fragment key={item.label}>
                {index > 0 && <span className="phs-bc-sep">/</span>}
                <button
              className={`phs-bc-item${item.active ? " active" : ""}`}
              onClick={item.active ? undefined : item.onClick}
              disabled={item.active}>
              
                  {item.label}
                </button>
              </React.Fragment>
          )}
          </div>
        }

        {!isTabMode &&
        <div className="phs-page-header">
            <h1 className="phs-page-title"><Trans i18nKey="onboarding.positionHistory" /></h1>
            <p className="phs-page-sub"><Trans i18nKey="onboarding.manageJobPositionAssignmentsOrganizationalChangesAndEf" /></p>
          </div>
        }

        <div className="phs-form-card">
          <div className="phs-section">
            <div className="phs-section-title"><Trans i18nKey="onboarding.assignmentDetails" /></div>
            <div className="phs-grid">
              <div className="phs-field">
                <label className="phs-label required"><Trans i18nKey="common.employeeId" /></label>
                <input className={`phs-input${errors.empId ? " error" : ""}`} name="empId" placeholder={i18n.t("onboarding.enterNumericEmployeeId")} value={form.empId} onChange={handleChange} readOnly={Boolean(initialEmployeeId)} />
                <p className="phs-hint"><Trans i18nKey="onboarding.numericEmployeeId" /></p>
                <p className="phs-error">{errors.empId || ""}</p>
              </div>
              <div className="phs-field">
                <label className="phs-label required"><Trans i18nKey="dateTime.effectiveDate" /></label>
                <input className={`phs-input${errors.effectiveDate ? " error" : ""}`} name="effectiveDate" type="date" value={form.effectiveDate} onChange={handleChange} />
                <p className="phs-hint"><Trans i18nKey="dateTime.selectTheEffectiveDateForThisPosition" /></p>
                <p className="phs-error">{errors.effectiveDate || ""}</p>
              </div>
              <div className="phs-field">
                <label className="phs-label required"><Trans i18nKey="onboarding.employeeType" /></label>
                <div className="phs-select-wrap">
                  <select className={`phs-select${errors.empType ? " error" : ""}`} name="empType" value={form.empType} onChange={handleChange}>
                    <option value=""><Trans i18nKey="onboarding.selectCategory" /></option>
                    {EMP_TYPES.map((type) =>
                    <option key={type} value={type}>{translateEmpTypeLabel(type)}</option>
                    )}
                  </select>
                </div>
                <p className="phs-hint"><Trans i18nKey="onboarding.selectTheEmploymentCategoryForThisPosition" /></p>
                <p className="phs-error">{errors.empType || ""}</p>
              </div>
              <div className="phs-field">
                <label className="phs-label required"><Trans i18nKey="onboarding.roleId" /></label>
                <input className={`phs-input${errors.roleId ? " error" : ""}`} name="roleId" placeholder={i18n.t("onboarding.enterRoleId")} value={form.roleId} onChange={handleChange} />
                <p className="phs-error">{errors.roleId || ""}</p>
              </div>
              <div className="phs-field">
                <label className="phs-label required"><Trans i18nKey="onboarding.designationId" /></label>
                <input className={`phs-input${errors.designationId ? " error" : ""}`} name="designationId" placeholder={i18n.t("onboarding.enterDesignationId")} value={form.designationId} onChange={handleChange} />
                <p className="phs-error">{errors.designationId || ""}</p>
              </div>
              <div className="phs-field">
                <label className="phs-label required"><Trans i18nKey="onboarding.departmentId" /></label>
                <input className={`phs-input${errors.departmentId ? " error" : ""}`} name="departmentId" placeholder={i18n.t("onboarding.enterDepartmentId")} value={form.departmentId} onChange={handleChange} />
                <p className="phs-error">{errors.departmentId || ""}</p>
              </div>
              <div className="phs-field">
                <label className="phs-label required"><Trans i18nKey="onboarding.gradeLevelId" /></label>
                <input className={`phs-input${errors.gradeLevelId ? " error" : ""}`} name="gradeLevelId" placeholder={i18n.t("onboarding.enterGradeLevelId")} value={form.gradeLevelId} onChange={handleChange} />
                <p className="phs-error">{errors.gradeLevelId || ""}</p>
              </div>
              <div className="phs-field">
                <label className="phs-label required"><Trans i18nKey="onboarding.divisionId" /></label>
                <input className={`phs-input${errors.divisionId ? " error" : ""}`} name="divisionId" placeholder={i18n.t("onboarding.enterDivisionId")} value={form.divisionId} onChange={handleChange} />
                <p className="phs-error">{errors.divisionId || ""}</p>
              </div>
              <div className="phs-field">
                <label className="phs-label required"><Trans i18nKey="onboarding.costCenterId" /></label>
                <input className={`phs-input${errors.costCenterId ? " error" : ""}`} name="costCenterId" placeholder={i18n.t("onboarding.enterCostCenterId")} value={form.costCenterId} onChange={handleChange} />
                <p className="phs-error">{errors.costCenterId || ""}</p>
              </div>
              <div className="phs-field">
                <label className="phs-label required"><Trans i18nKey="onboarding.workLocationId" /></label>
                <input className={`phs-input${errors.workLocationId ? " error" : ""}`} name="workLocationId" placeholder={i18n.t("onboarding.enterWorkLocationId")} value={form.workLocationId} onChange={handleChange} />
                <p className="phs-error">{errors.workLocationId || ""}</p>
              </div>
              <div className="phs-field">
                <label className="phs-label required"><Trans i18nKey="onboarding.managerId" /></label>
                <input className={`phs-input${errors.managerId ? " error" : ""}`} name="managerId" placeholder={i18n.t("onboarding.enterManagerId")} value={form.managerId} onChange={handleChange} />
                <p className="phs-error">{errors.managerId || ""}</p>
              </div>
            </div>
          </div>
          {(onBack || onNext || banner) &&
          <div className="phs-section">
              {banner &&
            <div className={`phs-banner ${banner.type}`} role="alert">
                  {banner.msg}
                </div>
            }
              {!isTabMode &&
            <div className="phs-actions">
                  {onBack ? <button className="phs-secondary-btn" type="button" onClick={onBack}><Trans i18nKey="formActions.back" /></button> : <span />}
                  <button className="phs-primary-btn" type="button" onClick={handleSave} disabled={loading}>
                    {loading ? i18n.t("localConveyance.saving") : i18n.t("formActions.save")}
                  </button>
                  {onNext ? <button className="phs-secondary-btn" type="button" onClick={handleFinish}><Trans i18nKey="onboarding.finish" /></button> : null}
                </div>
            }
            </div>
          }
        </div>
      </main>
    </div>);

}
